import React, { useState } from "react"
import {
	FaCalendarWeek,
	FaChartLine,
	FaCheck,
	FaChair,
	FaExclamationTriangle,
	FaExclamationCircle,
	FaChevronDown,
	FaChevronUp
} from "react-icons/fa"
import { isCenterClosed } from "../../../utils/generalUtils"

// Helper function to filter days when center is open
function filterOpenDays(futureHuddleData, centerOpen, centerClosedDates = []) {
	return futureHuddleData.filter(day => {
		const dateObj = new Date(day.date + "T12:00:00-08:00")
		const patients = day.metrics?.current_scheduled_patients || 0
		return !isCenterClosed(dateObj, centerOpen, centerClosedDates) && patients > 0
	})
}

function getHeaviestVolumeDay(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	let max = null
	let maxDay = null
	openDays.forEach(day => {
		if (day.metrics && typeof day.metrics.expected_patients === "number") {
			if (max === null || day.metrics.expected_patients > max) {
				max = day.metrics.expected_patients
				maxDay = day
			}
		}
	})
	return maxDay
}

function getPeakHour(heaviestDay) {
	if (!heaviestDay || !heaviestDay.graphData || !heaviestDay.graphData.utilizationCurves) {
		return "11 AM - 3 PM" // Default value if data is missing
	}

	// Get utilization data
	const scheduleData = heaviestDay.graphData.utilizationCurves.scheduleData || []

	// Sort by highest utilization (ongoing patient count)
	const sortedByUtilization = [...scheduleData].sort(
		(a, b) => (b.ongoing || 0) - (a.ongoing || 0)
	)

	// Get top 4 hours with highest utilization
	const topHours = sortedByUtilization.slice(0, 4)

	// If we don't have enough data, return default
	if (topHours.length === 0) {
		return "11 AM - 3 PM"
	}

	// Convert time values (minutes since midnight) to hour format
	const timeValues = topHours
		.map(slot => {
			const timeMinutes = typeof slot.time === "string" ? parseInt(slot.time, 10) : slot.time
			return timeMinutes / 60
		})
		.sort((a, b) => a - b) // Sort chronologically

	// If we have at least 2 time values, use the earliest and latest
	if (timeValues.length >= 2) {
		const startHour = timeValues[0]
		const endHour = timeValues[timeValues.length - 1] + 1 // Add 1 hour to include the full hour range

		// Format hours with AM/PM
		const formatHour = hour => {
			const hourNum = Math.floor(hour)
			const period = hourNum >= 12 ? "PM" : "AM"
			const formattedHour = hourNum % 12 === 0 ? 12 : hourNum % 12
			return `${formattedHour} ${period}`
		}

		return `${formatHour(startHour)} - ${formatHour(endHour)}`
	}

	return "11 AM - 3 PM" // Default fallback
}

function getDayClosestToTarget(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	let minDiff = 1000000
	let closestDay = null
	openDays.forEach(day => {
		if (day.metrics && day.metrics.current_scheduled_patients > 0) {
			const diff = Math.abs(
				day.metrics.target_patients - day.metrics.current_scheduled_patients
			)
			if (diff < minDiff) {
				minDiff = diff
				closestDay = {
					day,
					difference: diff,
					scheduled: day.metrics.current_scheduled_patients,
					target: day.metrics.target_patients
				}
			}
		}
	})
	return closestDay
}

function getDaysWithLowTemplateCompliance(
	futureHuddleData,
	centerOpen,
	centerClosedDates = [],
	settings
) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)

	// Get template compliance threshold from goals, default to 80 if not found
	const goals = settings?.goals || []
	const templateComplianceGoal = goals.find(g => g.id === "templateCompliance")
	const threshold = templateComplianceGoal ? parseFloat(templateComplianceGoal.value) : 85

	return openDays.filter(
		day =>
			day.metrics &&
			typeof day.metrics.template_compliance === "number" &&
			day.metrics.template_compliance < threshold
	)
}

function getAverageUtilization(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	const utilizations = openDays
		.map(day =>
			day.metrics && typeof day.metrics.chair_time_utilization === "number"
				? day.metrics.chair_time_utilization
				: null
		)
		.filter(x => x !== null)
	if (utilizations.length === 0) return 0
	return Math.round(utilizations.reduce((a, b) => a + b, 0) / utilizations.length)
}

function getTotalSchedulingConflicts(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	return openDays.reduce((sum, day) => {
		if (Array.isArray(day.schedulingIssues)) {
			return sum + day.schedulingIssues.length
		}
		return sum
	}, 0)
}

function getDaysWithMostConflicts(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	const days = openDays
		.map(day => ({
			date: day.date,
			conflicts: Array.isArray(day.schedulingIssues) ? day.schedulingIssues.length : 0
		}))
		.filter(day => day.conflicts > 0)
		.sort((a, b) => b.conflicts - a.conflicts)
		.slice(0, 2)

	return days.map(day => formatDayName(day.date)).join(" and ")
}

function formatDayName(dateStr) {
	return new Date(dateStr + "T12:00:00-08:00").toLocaleDateString(undefined, { weekday: "long" })
}

function formatDate(dateStr) {
	return new Date(dateStr + "T12:00:00-08:00").toLocaleDateString(undefined, {
		weekday: "long",
		month: "short",
		day: "numeric"
	})
}

function countDaysWithConflicts(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	return openDays.filter(
		day => Array.isArray(day.schedulingIssues) && day.schedulingIssues.length > 0
	).length
}

const WeekOverview = ({ futureHuddleData, settings }) => {
	const [showAll, setShowAll] = useState(false)
	const centerOpen = settings?.centerOpen || [true, true, true, true, true, true, true] // Default to all days open if not provided
	const centerClosedDates = settings?.center_closed_dates || []

	const heaviestDay = getHeaviestVolumeDay(futureHuddleData, centerOpen, centerClosedDates)
	const avgUtil = getAverageUtilization(futureHuddleData, centerOpen, centerClosedDates)
	const totalConflicts = getTotalSchedulingConflicts(
		futureHuddleData,
		centerOpen,
		centerClosedDates
	)
	const closestToTarget = getDayClosestToTarget(futureHuddleData, centerOpen, centerClosedDates)
	const lowComplianceDays = getDaysWithLowTemplateCompliance(
		futureHuddleData,
		centerOpen,
		centerClosedDates,
		settings
	)
	const daysWithConflicts = countDaysWithConflicts(
		futureHuddleData,
		centerOpen,
		centerClosedDates
	)
	const priorityDays = getDaysWithMostConflicts(futureHuddleData, centerOpen, centerClosedDates)
	const peakHour = heaviestDay ? getPeakHour(heaviestDay) : "11 AM - 3 PM"

	// Get utilization target from goals, default to 50 if not found
	const goals = settings?.goals || []
	const utilizationTargetGoal = goals.find(g => g.id === "utilizationTarget")
	const utilizationTarget = utilizationTargetGoal ? parseFloat(utilizationTargetGoal.value) : 50
	const utilizationDiff = utilizationTarget - avgUtil

	// Create an array of insights in the desired order
	const insights = [
		// 1. Chair Time Utilization
		{
			id: "utilization",
			component: (
				<div className="mb-4 bg-gradient-to-r from-gray-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-blue-500 transition-all hover:shadow-md">
					<div className="flex items-center mb-2">
						<div className="p-2 bg-blue-100 rounded-full mr-3">
							<FaChair className="text-blue-600" />
						</div>
						<span className="font-semibold text-gray-800">Chair Time Utilization</span>
					</div>
					<div className="ml-10 flex items-center">
						<span className="font-medium text-lg">{avgUtil}%</span>
						<span className="mx-2 text-gray-400">|</span>
						<span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full flex items-center">
							{utilizationDiff}% below target
						</span>
						<span className="text-gray-500 ml-2">({utilizationTarget}%)</span>
					</div>
					<div className="ml-10 text-sm text-gray-600 mt-1 flex items-center">
						<FaChartLine className="mr-1 text-gray-400" size={12} />
						Schedule more patients to increase chair time utilization
					</div>
				</div>
			)
		},
		// 2. Heaviest Volume
		heaviestDay && {
			id: "heaviest-volume",
			component: (
				<div className="mb-4 bg-gradient-to-r from-blue-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-blue-600 transition-all hover:shadow-md">
					<div className="flex items-center mb-2">
						<div className="p-2 bg-blue-100 rounded-full mr-3">
							<FaChartLine className="text-blue-600" />
						</div>
						<span className="font-semibold text-gray-800">Heaviest Volume</span>
					</div>
					<div className="ml-10">
						<span className="font-medium">{formatDate(heaviestDay.date)}</span>
						<span className="ml-2 bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full text-sm">
							{heaviestDay.metrics.expected_patients} patients
						</span>
					</div>
					<div className="ml-10 text-sm text-gray-600 mt-1 flex items-center">
						<FaCheck className="mr-1 text-blue-500" size={12} />
						Ensure max staffing during {peakHour} window
					</div>
				</div>
			)
		},
		// 3. Template Compliance
		lowComplianceDays.length > 0 && {
			id: "template-compliance",
			component: (
				<div className="mb-4 bg-gradient-to-r from-yellow-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-yellow-500 transition-all hover:shadow-md">
					<div className="flex items-center mb-2">
						<div className="p-2 bg-yellow-100 rounded-full mr-3">
							<FaExclamationTriangle className="text-yellow-600" />
						</div>
						<span className="font-semibold text-gray-800">Low Template Compliance</span>
					</div>
					<div className="ml-10">
						<span className="font-medium">Observed on:</span>
					</div>
					<div className="ml-10 flex flex-wrap gap-1 mt-1">
						{lowComplianceDays.map((day, index) => (
							<span
								key={index}
								className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs"
							>
								{formatDayName(day.date)}
							</span>
						))}
					</div>
					<div className="ml-10 text-sm text-gray-600 mt-2 flex items-center">
						<FaExclamationTriangle className="mr-1 text-yellow-500" size={12} />
						Review shift coverage and alignment with patient volume during peak hours
					</div>
				</div>
			)
		},
		// 4. Closest to Target
		closestToTarget && {
			id: "closest-target",
			component: (
				<div className="mb-4 bg-gradient-to-r from-green-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-green-500 transition-all hover:shadow-md">
					<div className="flex items-center mb-2">
						<div className="p-2 bg-green-100 rounded-full mr-3">
							<FaCheck className="text-green-600" />
						</div>
						<span className="font-semibold text-gray-800">Closest to Target Day</span>
					</div>
					<div className="ml-10">
						<span className="font-medium">{formatDate(closestToTarget.day.date)}</span>
						<div className="inline-flex items-center ml-2">
							<span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-l-full text-sm">
								{closestToTarget.scheduled} patients
							</span>
							<span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-r-full text-sm border-l border-green-300">
								Target: {closestToTarget.target}
							</span>
						</div>
					</div>
					<div className="ml-10 text-sm text-gray-600 mt-1 flex items-center">
						<FaCheck className="mr-1 text-green-500" size={12} />
						Excellent load matching – consider using as scheduling template baseline
					</div>
				</div>
			)
		},
		// 5. Scheduling Conflicts
		totalConflicts > 0 && {
			id: "conflicts",
			component: (
				<div className="mb-4 bg-gradient-to-r from-red-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-red-500 transition-all hover:shadow-md">
					<div className="flex items-center mb-2">
						<div className="p-2 bg-red-100 rounded-full mr-3">
							<FaExclamationCircle className="text-red-600" />
						</div>
						<span className="font-semibold text-gray-800">Scheduling Conflicts</span>
					</div>
					<div className="ml-10 flex items-center">
						<span className="bg-red-200 text-red-800 px-2 py-0.5 rounded-full text-sm font-medium">
							{totalConflicts} issues
						</span>
						<span className="ml-2">across {daysWithConflicts} days</span>
					</div>
					<div className="ml-10 text-sm text-gray-600 mt-1 flex items-center">
						<FaExclamationCircle className="mr-1 text-red-500" size={12} />
						Prioritize {priorityDays} for resolution
					</div>
				</div>
			)
		}
	].filter(Boolean) // Filter out null/undefined items

	// Display limited insights or all based on showAll state
	const visibleInsights = showAll ? insights : insights.slice(0, 2)

	return (
		<div className="bg-white rounded-lg shadow p-6 flex flex-col min-h-[220px]">
			<h2 className="text-2xl font-semibold mb-4 flex items-center">
				<FaCalendarWeek className="mr-2" style={{ color: "#352D66" }} />
				<span style={{ color: "#352D66" }}>Week Overview</span>
			</h2>

			<div>
				{visibleInsights.map(insight => (
					<React.Fragment key={insight.id}>{insight.component}</React.Fragment>
				))}

				{insights.length > 3 && (
					<button
						onClick={() => setShowAll(!showAll)}
						className="w-full py-2 mt-1 flex items-center justify-center rounded-md duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						style={{
							background:
								"linear-gradient(90deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.05) 100%)",
							borderColor: "rgba(99,102,241,0.2)",
							color: "rgb(99,102,241)",
							border: "1px solid rgba(99,102,241,0.2)"
						}}
					>
						{showAll ? (
							<>
								<span className="font-medium">Show Less</span>
								<FaChevronUp className="ml-1" />
							</>
						) : (
							<>
								<span className="font-medium">Show More</span>
								<FaChevronDown className="ml-1" />
							</>
						)}
					</button>
				)}
			</div>
		</div>
	)
}

export default WeekOverview
